import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { Button } from './Button';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
  maxWidth?: 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl';
}

export const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  title,
  children,
  maxWidth = 'md',
}) => {
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
    }
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const widthStyles = {
    sm: 'max-w-sm',
    md: 'max-w-md',
    lg: 'max-w-lg',
    xl: 'max-w-xl',
    '2xl': 'max-w-2xl',
    '3xl': 'max-w-3xl',
  };

  const modalContent = (
    <div
      className="fixed inset-0 !m-0 z-[60] flex items-center justify-center bg-black/50 backdrop-blur-xs p-2.5 sm:p-4 animate-in fade-in-50 duration-150"
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className={`bg-cf-surface border border-cf-border rounded-lg shadow-2xl w-full ${widthStyles[maxWidth]} animate-in zoom-in-95 overflow-hidden`}
      >
        <div className="flex items-center justify-between px-3.5 sm:px-5 py-2.5 sm:py-3.5 border-b border-cf-border bg-cf-surface">
          <h3 className="text-xs sm:text-sm font-semibold text-cf-textPrimary truncate mr-2">{title}</h3>
          <Button
            variant="ghost"
            size="icon-xs"
            onClick={onClose}
            aria-label="Close modal"
            className="text-cf-textSecondary hover:text-cf-textPrimary shrink-0"
          >
            <X size={15} />
          </Button>
        </div>
        <div className="p-3 sm:p-5 max-h-[85vh] overflow-y-auto">{children}</div>
      </div>
    </div>
  );

  return typeof document !== 'undefined' ? createPortal(modalContent, document.body) : modalContent;
};
